import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import CartDrawer from "@/components/CartDrawer";
import FloatingCart from "@/components/FloatingCart";
import { api, trackWaClick } from "@/lib/api";
import { rupiah } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { motion } from "framer-motion";
import { Plane, Search, ArrowLeft, MessageCircle, Info, ExternalLink } from "lucide-react";

const WA_ADMIN = "6281377114000"; // Nomor WhatsApp admin INDOTRAVEL

const AIRPORTS = [
  { code: "CGK", city: "Jakarta" }, { code: "SUB", city: "Surabaya" }, { code: "DPS", city: "Denpasar (Bali)" },
  { code: "YIA", city: "Yogyakarta" }, { code: "BDO", city: "Bandung" }, { code: "KNO", city: "Medan" },
  { code: "UPG", city: "Makassar" }, { code: "PLM", city: "Palembang" }, { code: "PDG", city: "Padang" },
  { code: "BPN", city: "Balikpapan" }, { code: "PNK", city: "Pontianak" }, { code: "MDC", city: "Manado" },
  { code: "SIN", city: "Singapura" }, { code: "KUL", city: "Kuala Lumpur" },
];

function googleFlightsUrl(from, to, depart, ret, pax) {
  let q = `flights from ${from} to ${to}`;
  if (depart) q += ` on ${depart}`;
  if (ret) q += ` returning ${ret}`;
  else if (depart) q += " oneway";
  if (pax && pax !== "1") q += ` for ${pax} adults`;
  return `https://www.google.com/travel/flights?q=${encodeURIComponent(q)}`;
}

function routeCode(label) {
  const m = label.match(/\(([A-Z]{3})\)/);
  return m ? m[1] : label;
}

export default function IndoTravel() {
  const [flights, setFlights] = useState([]);
  const [q, setQ] = useState("");
  const [from, setFrom] = useState("CGK");
  const [to, setTo] = useState("DPS");
  const [depart, setDepart] = useState("");
  const [ret, setRet] = useState("");
  const [trip, setTrip] = useState("oneway");
  const [pax, setPax] = useState("1");

  useEffect(() => {
    api.get("/indotravel/flights").then((r) => setFlights(r.data)).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return flights;
    return flights.filter(
      (f) =>
        f.route_from.toLowerCase().includes(s) ||
        f.route_to.toLowerCase().includes(s) ||
        f.airline.toLowerCase().includes(s)
    );
  }, [flights, q]);

  const waLink = (f) =>
    `https://wa.me/${WA_ADMIN}?text=${encodeURIComponent(
      `Halo Admin INDOTRAVEL, saya mau booking tiket ${f.route_from} → ${f.route_to} (${f.airline}, ± ${rupiah(f.price)}). Mohon info jadwal & ketersediaan. Terima kasih!`
    )}`;

  return (
    <div className="min-h-screen paper-bg">
      <Navbar />
      <CartDrawer />
      <FloatingCart />

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 pt-8 pb-4">
        <Link to="/" data-testid="back-to-home-btn" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-emerald-700 text-white text-sm font-bold shadow-md shadow-emerald-700/25 hover:bg-emerald-800 hover:shadow-lg hover:-translate-y-0.5 transition">
          <ArrowLeft className="w-4 h-4" /> Kembali Belanja
        </Link>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mt-4 relative overflow-hidden rounded-3xl bg-gradient-to-br from-rose-600 via-orange-600 to-amber-600 text-white p-8 md:p-12"
        >
          <img
            src="https://images.unsplash.com/photo-1507812984078-917a274065be?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwzfHxhaXJwbGFuZSUyMHdpbmRvdyUyMHdpbmclMjBza3klMjB0cmF2ZWx8ZW58MHx8fHwxNzg3Njc1MTcyfDA&ixlib=rb-4.1.0&q=85"
            alt=""
            className="absolute inset-0 w-full h-full object-cover opacity-25"
          />
          <div className="relative max-w-2xl">
            <span className="tag-pill bg-white/20 text-white">
              <Plane className="w-3 h-3" /> INDOTRAVEL
            </span>
            <h1 className="mt-5 font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              Tiket pesawat <span className="text-amber-200">termurah</span>, booking via WhatsApp.
            </h1>
            <p className="mt-5 text-orange-50/90 text-sm md:text-base leading-relaxed max-w-lg">
              Bandingkan harga tiket berbagai maskapai, lalu booking langsung ke admin INDOTRAVEL — dibantu sampai tiket terbit.
            </p>
            <a
              href="#cek-harga"
              data-testid="indotravel-cta"
              className="mt-7 inline-block px-6 py-3 rounded-full bg-white text-rose-700 font-bold hover:bg-orange-50 transition shadow-lg"
            >
              Cek Harga Sekarang ↓
            </a>
          </div>
        </motion.div>
      </section>

      {/* Cek harga via Google Flights */}
      <section id="cek-harga" className="max-w-7xl mx-auto px-4 md:px-8 pt-6">
        <div className="rounded-3xl bg-white border border-[#E5DECF] p-6 md:p-8">
          <h2 className="font-display font-black text-2xl md:text-3xl tracking-tight text-stone-900">Cek Harga Tiket Pesawat</h2>
          <p className="text-sm text-stone-600 mt-1">Pilih rute & tanggal — harga live dibuka di <b>Google Flights</b>, lalu booking dibantu admin INDOTRAVEL via WhatsApp.</p>

          <div className="mt-4 flex gap-2">
            {[["oneway", "Sekali Jalan"], ["roundtrip", "Pulang-Pergi"]].map(([v, label]) => (
              <button
                key={v}
                data-testid={`gf-trip-${v}`}
                onClick={() => setTrip(v)}
                className={`px-4 py-2 rounded-full text-sm font-semibold border transition ${
                  trip === v ? "bg-rose-600 text-white border-rose-600" : "bg-white text-stone-600 border-[#E5DECF] hover:border-rose-400"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            <div>
              <label className="text-[10px] font-semibold text-stone-500 uppercase tracking-widest">Dari</label>
              <select data-testid="gf-from" value={from} onChange={(e) => setFrom(e.target.value)} className="mt-1 w-full rounded-xl border border-[#E5DECF] px-3 py-2.5 text-sm bg-white">
                {AIRPORTS.map((a) => <option key={a.code} value={a.code}>{a.city} ({a.code})</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-semibold text-stone-500 uppercase tracking-widest">Ke</label>
              <select data-testid="gf-to" value={to} onChange={(e) => setTo(e.target.value)} className="mt-1 w-full rounded-xl border border-[#E5DECF] px-3 py-2.5 text-sm bg-white">
                {AIRPORTS.map((a) => <option key={a.code} value={a.code}>{a.city} ({a.code})</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-semibold text-stone-500 uppercase tracking-widest">Berangkat</label>
              <Input data-testid="gf-depart" type="date" value={depart} onChange={(e) => setDepart(e.target.value)} className="mt-1" />
            </div>
            {trip === "roundtrip" && (
              <div>
                <label className="text-[10px] font-semibold text-stone-500 uppercase tracking-widest">Pulang</label>
                <Input data-testid="gf-return" type="date" value={ret} onChange={(e) => setRet(e.target.value)} className="mt-1" />
              </div>
            )}
            <div>
              <label className="text-[10px] font-semibold text-stone-500 uppercase tracking-widest">Penumpang</label>
              <select data-testid="gf-pax" value={pax} onChange={(e) => setPax(e.target.value)} className="mt-1 w-full rounded-xl border border-[#E5DECF] px-3 py-2.5 text-sm bg-white">
                {[1, 2, 3, 4, 5, 6].map((n) => <option key={n} value={String(n)}>{n} orang</option>)}
              </select>
            </div>
          </div>

          {from === to && (
            <p data-testid="gf-same-route-warning" className="mt-3 text-sm text-rose-600 font-semibold">Kota asal dan tujuan tidak boleh sama.</p>
          )}

          <div className="mt-5 flex flex-wrap gap-3">
            <a
              data-testid="gf-check-btn"
              href={googleFlightsUrl(from, to, depart, trip === "roundtrip" ? ret : "", pax)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => from === to && e.preventDefault()}
              className={`px-6 py-3 rounded-full font-bold flex items-center gap-2 transition shadow-lg ${
                from === to ? "bg-stone-300 text-stone-500 cursor-not-allowed" : "bg-rose-600 text-white hover:bg-rose-700 shadow-rose-600/30"
              }`}
            >
              <ExternalLink className="w-4 h-4" /> Cek Harga di Google Flights
            </a>
            <a
              data-testid="gf-ask-admin-btn"
              href={`https://wa.me/${WA_ADMIN}?text=${encodeURIComponent(`Halo Admin INDOTRAVEL, saya mau cek & booking tiket ${from} → ${to}${depart ? ` tanggal ${depart}` : ""} untuk ${pax} orang. Mohon dibantu ya!`)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="px-6 py-3 rounded-full bg-emerald-600 text-white font-bold hover:bg-emerald-700 transition flex items-center gap-2"
            >
              <MessageCircle className="w-4 h-4" /> Tanya Admin via WA
            </a>
          </div>
        </div>
      </section>

      {/* Rute populer */}
      <section id="daftar-tiket" className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="font-display font-black text-3xl md:text-4xl tracking-tight text-stone-900">Rute Populer</h2>
            <p className="text-xs text-stone-500 mt-1">Shortcut cek harga rute favorit via Google Flights.</p>
          </div>
          <div className="relative w-full sm:w-80">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
            <Input
              data-testid="indotravel-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Cari rute atau maskapai..."
              className="pl-9 rounded-full"
            />
          </div>
        </div>

        <div className="mt-6 space-y-3">
          {filtered.length === 0 && (
            <p className="text-sm text-stone-500">Tidak ada rute yang cocok. Coba kata kunci lain.</p>
          )}
          {filtered.map((f, i) => (
            <motion.div
              key={f.id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, delay: i * 0.04 }}
              data-testid={`indotravel-flight-${f.id}`}
              className="bg-white rounded-3xl border border-[#E5DECF] p-5 flex flex-wrap items-center gap-4 justify-between card-tilt"
            >
              <div className="flex items-center gap-4">
                <div className="w-11 h-11 rounded-2xl bg-rose-100 text-rose-600 flex items-center justify-center shrink-0">
                  <Plane className="w-5 h-5" />
                </div>
                <div>
                  <div className="font-display font-bold text-lg leading-tight">{f.route_from} → {f.route_to}</div>
                  <div className="text-xs text-stone-500">{f.airline} · {f.duration}</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <a
                  data-testid={`indotravel-gf-${f.id}`}
                  href={googleFlightsUrl(routeCode(f.route_from), routeCode(f.route_to), "", "", "1")}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2.5 rounded-full bg-sky-600 text-white font-bold text-sm hover:bg-sky-700 transition flex items-center gap-2"
                >
                  <ExternalLink className="w-4 h-4" /> Cek Harga
                </a>
                <a
                  data-testid={`indotravel-book-wa-${f.id}`}
                  href={waLink(f)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-5 py-2.5 rounded-full bg-emerald-600 text-white font-bold text-sm hover:bg-emerald-700 transition flex items-center gap-2"
                >
                  <MessageCircle className="w-4 h-4" /> Booking via WA
                </a>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="mt-6 rounded-2xl bg-amber-50 border border-amber-200 p-4 flex gap-3 text-sm text-amber-900">
          <Info className="w-5 h-5 shrink-0 mt-0.5" />
          <p>
            Klik <b>Cek Harga</b> untuk melihat harga live di Google Flights. Setelah ketemu harga terbaik, klik <b>Booking via WA</b> — admin INDOTRAVEL bantu terbitkan tiket sampai jadi.
          </p>
        </div>
      </section>

      <footer className="relative border-t border-[#E5DECF] py-6 text-center text-xs text-stone-500 font-mono uppercase tracking-widest">
        <a data-testid="footer-wa-link" onClick={() => trackWaClick("indotravel")} href={`https://wa.me/${WA_ADMIN}?text=Halo%20Admin%20INDOTRAVEL%2C%20saya%20mau%20tanya%20tiket...`} target="_blank" rel="noopener noreferrer" className="mb-3 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-700 text-white text-sm font-bold normal-case tracking-normal hover:bg-emerald-800 transition">
          <MessageCircle className="w-4 h-4" /> Admin FAS Group Corp.
        </a>
        <div>INDOTRAVEL by FAS Group · Booking dibantu admin via WhatsApp</div>
        <img src="https://customer-assets-7cd3h4nn.emergentagent.net/job_fasmart-online/artifacts/kpyu0nvc_ChatGPT%20Image%2026%20Agu%202026%2C%2012.19.43.png" alt="FAS Group Corp" data-testid="footer-fasgroup-logo" className="mx-auto mt-4 h-14 w-auto object-contain rounded-xl bg-white p-1.5 border border-[#E5DECF] md:absolute md:right-6 md:bottom-4 md:m-0 md:h-16" />
      </footer>
    </div>
  );
}
